import { getByID } from '@adornis/baseql/operations/mongo.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { buildFullOptions, type DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { DataTable } from '@adornis/data-view/client/table/data-table.js';
import { View } from '@adornis/data-view/db/view.js';
import { EntityPickerInput } from '@adornis/entity-picker/client/entity-picker-input.js';
import { FieldConfiguration } from '@adornis/field-management/field-configuration.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import { IntAspect } from '@adornis/filter/aspect-schemas/IntAspect.js';
import { ListAspect } from '@adornis/filter/aspect-schemas/ListAspect.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { ToggleInput } from '@adornis/formfield/fields/toggle-input.js';
import { stateful, useMemo, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { html, nothing } from 'lit';
import { SgSnackbar } from '../../../client/components/ui/sg-snackbar.js';
import { Person } from '../../entities/person.js';
import { UserListAPI } from '../../list/api/UserListAPI.js';
import { PersonImportAPI } from '../api/PersonImportAPI.js';
import {
  getEditableImportColumns,
  IMPORT_FIELD_LABELS,
  importValueToCsv,
  PersonImportData,
} from '../entities/PersonImportConvertConfig.js';
import {
  changedFieldSpecifiers,
  CHILD_ID_FIELD_SPECIFIER_ID,
  IMPORT_FINDING_CATEGORIES,
  ImportCsvCell,
  ImportFindingSeverity,
  PersonImportStrategy,
  type ImportFieldSpecifier,
  type ImportFinding,
  type ImportFindingCode,
  type PersonImportMatchedBy,
} from '../entities/PersonImportStrategy.js';
import { openPersonImportRowEditDialog } from './PersonImportRowEditDialog.js';

/**
 * Which rows the table shows: those that had a severity or a finding code at any point of this process,
 * or all rows when neither is set. Filtered on what the rows *had*, not what they have - see
 * `PersonImportStrategy.reviewCodes` - so a fixed row stays in the list, marked as fixed.
 */
export interface ImportReviewFilter {
  severity?: ImportFindingSeverity;
  code?: ImportFindingCode;
  /** a text that appears anywhere in the row - see {@link SEARCH_FIELDS} */
  search?: string;
}

/**
 * Where the search looks: the csv row as the file had it - every column, the guardians' columns too, so a
 * surname finds the whole family -, the values as corrected in the overview, and the matched person.
 */
const SEARCH_FIELDS = [
  'rawImportData.rawCsvJson',
  'rawImportData.stammdaten_vorname',
  'rawImportData.stammdaten_nachname',
  'rawImportData.email',
  'resolvedLabel',
];

const EDITABLE_COLUMNS = getEditableImportColumns();

// module-level so the table's memoized options keep a stable column reference
const QUICK_FIX_FIELD = new FieldConfiguration({ customID: 'quickFix' });

/** what the quick fix and the edit dialog read from a row */
export const ROW_EDIT_SELECTION = {
  rawImportData: {
    isSbS: 1,
    stammdaten_vorname: 1,
    stammdaten_nachname: 1,
    ...Object.fromEntries(EDITABLE_COLUMNS.map(({ field }) => [field, 1])),
  },
  fileConflicts: { field: 1, values: 1 },
} as const;

/** what the details dialog reads from a row */
const ROW_DIALOG_SELECTION = {
  ...ROW_EDIT_SELECTION,
  resolvedId: 1,
  matchConfidence: 1,
  matchedBy: 1,
  duplicateMatchIDs: 1,
  childNames: 1,
  error: 1,
  findings: { severity: 1, code: 1, message: 1 },
  fieldSpecifiers: { csvFieldName: 1, dbFieldPath: 1, from: 1, to: 1 },
} as const;

/**
 * Writes the user's correction and recalculates the row. The returned rows are merged by the client into
 * the very entities the table holds, so a re-render shows them - the table itself is not queried again.
 */
export async function editImportRow(strategy: PersonImportStrategy, cells: { column: string; value: string }[]) {
  try {
    await PersonImportAPI.editRow(
      strategy._id,
      cells.map(cell => new ImportCsvCell(cell)),
    )(selectionSet(() => PersonImportStrategy, 2));
  } catch (error) {
    void SgSnackbar.showError(error instanceof Error ? error.message : 'Die Korrektur konnte nicht übernommen werden.');
    throw error;
  }
}

/**
 * The person a row updates, or nobody for a new one. Picking is a decision: the row is never matched
 * again and recalculated at once - see `assignImportPerson`.
 */
export function ImportPersonPicker(strategy: PersonImportStrategy, afterPick: () => void) {
  return EntityPickerInput({
    disabled: !!strategy.error,
    placeholder: 'Neue Person anlegen',
    placeholderMode: 'hide',
    value: strategy.resolvedId,
    class: Person,
    clearable: true,
    operation: UserListAPI.subscribe,
    filter: new AdornisFilter({}),
    renderString: entity =>
      [
        entity.debitorennummer,
        `${entity.profilDaten?.stammdaten?.vorname ?? ''} ${entity.profilDaten?.stammdaten?.nachname ?? ''}`.trim(),
      ]
        .filter(Boolean)
        .join(' · '),
    renderItem: entity => html`
      <div class="flex flex-col justify-center gap-0.5 p-2">
        <b>
          ${entity.profilDaten?.stammdaten?.vorname ?? 'Kein Vorname'}
          ${entity.profilDaten?.stammdaten?.nachname ?? 'Kein Nachname'}
        </b>
        <span class="text-base-content/70">${entity.debitorennummer} · ${entity.email}</span>
      </div>
    `,
    selectionSet: {
      _id: 1,
      debitorennummer: 1,
      email: 1,
      profilDaten: { stammdaten: { vorname: 1, nachname: 1 } },
    },
    onPicked: async value => {
      try {
        await PersonImportAPI.assignPerson(strategy._id, value ?? null)(selectionSet(() => PersonImportStrategy, 2));
      } catch (error) {
        void SgSnackbar.showError(error instanceof Error ? error.message : String(error));
      }
      afterPick();
    },
  });
}

/**
 * The further records of the same person (same email and name) that the import only links to the children.
 */
export const DuplicateMatches = stateful((props: { ids: string[] }) => {
  const { value: duplicates } = useQuery(
    () =>
      getByID(
        Person,
        props.ids,
      )({ _id: 1, debitorennummer: 1, profilDaten: { stammdaten: { vorname: 1, nachname: 1 } } }),
    [props.ids.join(',')],
  );

  return html`
    <p class="label flex-wrap">
      Doppelt angelegt, erhalten nur die Verknüpfung zu den Kindern:
      ${(duplicates ?? []).map(
        duplicate => html`
          <a class="link" href="/personen/${duplicate._id}" target="_blank" rel="noopener">
            ${duplicate.debitorennummer} ${duplicate.profilDaten?.stammdaten?.vorname ?? ''}
            ${duplicate.profilDaten?.stammdaten?.nachname ?? ''}
          </a>
        `,
      )}
    </p>
  `;
});

/**
 * How sure the import is about what it decided for this row - not how alike two names are. A confident
 * "creates a new person" is as green as a confident update; only a row that could go either way is red.
 */
export const getConfidenceUi = (confidenceValue: number | null | undefined) => {
  const confidence = Math.max(0, Math.min(1, confidenceValue ?? 0));

  if (confidence >= 0.9) return { confidence, badgeClass: 'badge-success' };
  if (confidence >= 0.5) return { confidence, badgeClass: 'badge-warning' };

  return { confidence, badgeClass: 'badge-error' };
};

/**
 * How a finding severity reads in the overview. The row takes the colour of its worst finding, so the
 * rows that need a decision are the ones that stand out.
 */
export const SEVERITY_UI = {
  [ImportFindingSeverity.FEHLER]: {
    label: 'Fehler',
    badgeClass: 'badge-error',
    alertClass: 'alert-error',
    icon: html`<span class="icon-[material-symbols--flag-rounded] size-5 shrink-0"></span>`,
    rowClass: 'bg-error/10 hover:bg-error/15',
  },
  [ImportFindingSeverity.WARNUNG]: {
    label: 'Warnung',
    badgeClass: 'badge-warning',
    alertClass: 'alert-warning',
    icon: html`<span class="icon-[material-symbols--warning-rounded] size-5 shrink-0"></span>`,
    rowClass: 'bg-warning/10 hover:bg-warning/15',
  },
  [ImportFindingSeverity.HINWEIS]: {
    label: 'Hinweis',
    badgeClass: 'badge-ghost',
    alertClass: '',
    icon: html`<span class="icon-[material-symbols--info-outline-rounded] size-5 shrink-0"></span>`,
    rowClass: 'hover:bg-base-200/60',
  },
} as const;

export const getSeverityUi = (severity: number | null | undefined) =>
  SEVERITY_UI[(severity ?? ImportFindingSeverity.HINWEIS) as ImportFindingSeverity] ??
  SEVERITY_UI[ImportFindingSeverity.HINWEIS];

/** what a finding is called in the overview - its category, or its severity for one without a code */
export const findingLabel = (finding: ImportFinding) =>
  (finding.code && IMPORT_FINDING_CATEGORIES[finding.code]?.label) || getSeverityUi(finding.severity).label;

/**
 * What actually identified the person. A percentage on its own says how sure the import is but not what
 * it went on - and a key hit and a name guess are worth very different amounts of trust.
 */
export const MATCHED_BY_LABEL: Record<PersonImportMatchedBy, string> = {
  resolvedId: 'manuell zugeordnet',
  externalUniqueID: 'über Externe ID',
  debitorennummer: 'über Debitorennummer',
  email: 'über E-Mail',
  name: 'über Namen',
};

/** a field as the overview names it, without the format hint the edit dialog carries */
export const specifierLabel = (specifier: ImportFieldSpecifier) =>
  specifier.dbFieldPath === CHILD_ID_FIELD_SPECIFIER_ID
    ? 'Kinder'
    : (IMPORT_FIELD_LABELS[specifier.csvFieldName as keyof PersonImportData] ?? specifier.csvFieldName ?? '').replace(
        / \(.*\)$/,
        '',
      );

/**
 * A labelled cell: a fixed label column, so the labels of all rows line up, and values at full contrast.
 * The table itself renders at `text-xs`; the cells read better a step larger.
 */
const CELL_COLUMNS = 'grid min-w-0 grid-cols-[6.5rem_minmax(0,1fr)] items-center gap-x-3 gap-y-1';
const CELL_GRID = `${CELL_COLUMNS} w-full self-start px-3 py-2 text-sm`;
const CELL_LABEL = 'truncate font-semibold text-base-content/70';

/** the child link lists person ids - what matters is how many are new */
const childLinkChange = (specifier: ImportFieldSpecifier) => {
  const before = (specifier.from ?? '').split(', ').filter(Boolean);
  const added = (specifier.to ?? '').split(', ').filter(id => id && !before.includes(id));
  return `+${added.length}`;
};

export const PersonImportStrategyDataTable = stateful(
  (props: { processId: string; fields: FieldConfiguration[]; review?: ImportReviewFilter }) => {
    const [, rerenderRows] = useState({});
    const { severity, code, search } = props.review ?? {};

    /**
     * The columns that fix the filtered code, as inputs right in the row - "every faulty email at once".
     */
    const quickFixColumns = code ? IMPORT_FINDING_CATEGORIES[code].columns : [];
    const quickFixConfirmsPerson = !!code && !!IMPORT_FINDING_CATEGORIES[code].confirmsPerson;

    /**
     * Built once per process and filter. The dialog above re-renders on every strategy change (live
     * counts), and a rebuilt View carries a new AdornisFilter with a fresh random `_id` - which is part
     * of the pagination cache key, so the table would reset and refetch on every toggle.
     */
    const dataViewOptions = useMemo(
      () => [
        {
          class: PersonImportStrategy,
          operation: PersonImportAPI.getStrategies,
          isSortable: false,
          isEntryClickable: false,
          /**
           * The final selection set is this one merged with the columns' - what `rowClass` reads is not
           * added automatically, and reading a field the query left out throws for every row.
           */
          selectionSet: { severity: 1, approved: 1 },
          rowClass: (strategy: PersonImportStrategy) =>
            `${getSeverityUi(strategy.severity).rowClass} border-b border-base-300 transition-colors`,
          view: new View({
            filter: new AdornisFilter({
              search: search ?? null,
              searchFields: SEARCH_FIELDS,
              aspects: [
                new StringAspect({ fieldName: 'importProcessID', value: props.processId, mode: 'equals' }),
                /**
                 * A matched row that writes nothing needs no decision - it is only counted in the summary
                 * above the table, unless its person is stored twice. Filtered by `listed` rather than
                 * `unchanged`, so a row an edit made unchanged stays. See {@link PersonImportStrategy.listed}.
                 */
                new BooleanAspect({ fieldName: 'listed', value: true }),
                ...(severity !== undefined
                  ? [new IntAspect({ fieldName: 'peakSeverity', mode: 'equals', min: severity })]
                  : []),
                ...(code ? [new ListAspect({ fieldName: 'reviewCodes', values: [code] })] : []),
              ],
              /**
               * The order is decided once, when the strategies are written (worst findings first), and
               * stored - sorting by anything the user can change here would make rows jump away under the
               * cursor whenever a person is assigned. See {@link PersonImportStrategy.sortIndex}.
               */
              sortingFields: ['sortIndex'],
              sortingDirections: ['asc'],
            }),
            configuredColumns:
              quickFixColumns.length || quickFixConfirmsPerson
                ? props.fields.flatMap(field => (field.customID === 'findings' ? [field, QUICK_FIX_FIELD] : [field]))
                : props.fields,
          }),

          customColumns: [
            {
              customID: 'approved',
              columnName: 'Freigabe',
              columnWidth: '90px',
              selectionSet: { approved: 1 },
              renderItem: (strategy: PersonImportStrategy) => html`
                <div class="px-3 py-2 self-start">
                  ${ToggleInput({
                    value: !!strategy.approved,
                    onChanged: async checked => {
                      strategy.approved = checked;
                      await strategy.save();
                      rerenderRows({});
                    },
                  })}
                </div>
              `,
            },
            {
              customID: 'person',
              columnName: 'Zeile → Person',
              columnWidth: 'minmax(18rem, 1.3fr)',
              selectionSet: {
                resolvedId: 1,
                resolvedLabel: 1,
                childNames: 1,
                duplicateMatchIDs: 1,
                approved: 1,
                error: 1,
                ...ROW_EDIT_SELECTION,
              },
              renderItem: (strategy: PersonImportStrategy) => {
                const data = strategy.rawImportData;
                const name = `${data?.stammdaten_vorname ?? ''} ${data?.stammdaten_nachname ?? ''}`.trim();
                // not released yet: the same operation, outlined - the row still says what it would do
                const operationClass = strategy.resolvedId
                  ? strategy.approved
                    ? 'badge-primary'
                    : 'badge-primary badge-outline'
                  : strategy.approved
                    ? 'badge-success'
                    : 'badge-success badge-outline';

                return html`
                  <div class="${CELL_GRID}">
                    <span class="${CELL_LABEL}">Datei</span>
                    <span class="flex min-w-0 items-center gap-1.5">
                      <span class="truncate font-semibold">${name || '—'}</span>
                      ${data?.isSbS ? html`<span class="badge badge-ghost badge-sm shrink-0">Sorgeb.</span>` : nothing}
                    </span>

                    <span class="${CELL_LABEL}">Datenbank</span>
                    <!-- inline rather than a floating dropdown: the table scrolls, and a dropdown would be cut off at its edge -->
                    <details class="min-w-0">
                      <summary
                        class="flex min-w-0 cursor-pointer list-none items-baseline gap-1.5 [&::-webkit-details-marker]:hidden"
                      >
                        <span class="badge badge-sm shrink-0 ${operationClass}">
                          ${strategy.resolvedId ? 'Update' : 'Neu'}
                        </span>
                        <!-- the person itself is the link to change it - no separate "ändern" eating the width -->
                        <span class="link link-primary link-hover min-w-0 break-words" title="Person ändern">
                          ${strategy.resolvedLabel || (strategy.resolvedId ? 'bestehende Person' : 'wird neu angelegt')}
                        </span>
                      </summary>
                      <div class="mt-1">${ImportPersonPicker(strategy, () => rerenderRows({}))}</div>
                    </details>

                    ${
                      strategy.childNames?.length
                        ? html`
                            <span class="${CELL_LABEL}">Kinder</span>
                            <span class="truncate text-base-content/70">${strategy.childNames.join(', ')}</span>
                          `
                        : nothing
                    }
                    ${
                      // only the count - which records they are is in the details dialog, so rows need no person query
                      strategy.duplicateMatchIDs?.length
                        ? html`
                            <span class="${CELL_LABEL}">Dubletten</span>
                            <span class="text-warning">${strategy.duplicateMatchIDs.length} weitere Datensätze</span>
                          `
                        : nothing
                    }
                  </div>
                `;
              },
            },
            {
              customID: 'changes',
              columnName: 'Änderungen',
              columnWidth: 'minmax(22rem, 1.7fr)',
              selectionSet: {
                fieldSpecifiers: { csvFieldName: 1, dbFieldPath: 1, from: 1, delete: 1, to: 1 },
              },
              renderItem: (strategy: PersonImportStrategy) => {
                const fieldSpecifiers = strategy.fieldSpecifiers ?? [];
                const changed = changedFieldSpecifiers(fieldSpecifiers);
                // everything the row carries that the person already has - the rest of the dataset, on demand
                const same = fieldSpecifiers.filter(field => !changed.includes(field));

                return html`
                  <div class="${CELL_GRID}">
                    ${changed.map(
                      field => html`
                        <span class="${CELL_LABEL}" title=${specifierLabel(field)}>${specifierLabel(field)}</span>
                        ${
                          field.dbFieldPath === CHILD_ID_FIELD_SPECIFIER_ID
                            ? html`<span class="font-semibold">${childLinkChange(field)} verknüpft</span>`
                            : html`
                                <span class="flex min-w-0 items-center gap-1.5">
                                  ${
                                    field.from
                                      ? html`
                                          <span class="truncate text-base-content/70 line-through">${field.from}</span>
                                          <span
                                            class="icon-[material-symbols--arrow-forward-rounded] shrink-0 text-base-content/70"
                                          ></span>
                                        `
                                      : nothing
                                  }
                                  <span class="truncate font-semibold">${field.delete ? 'entfernen' : field.to}</span>
                                </span>
                              `
                        }
                      `,
                    )}
                    ${changed.length ? nothing : html`<span class="col-span-2 text-base-content/70">Keine Änderungen</span>`}
                    ${
                      same.length
                        ? html`
                            <details class="col-span-2">
                              <summary
                                class="link link-hover w-fit list-none text-base-content/70 [&::-webkit-details-marker]:hidden"
                              >
                                ${same.length} unveränderte Felder
                              </summary>
                              <div class="${CELL_COLUMNS} mt-1">
                                ${same.map(
                                  field => html`
                                    <span class="${CELL_LABEL}" title=${specifierLabel(field)}>
                                      ${specifierLabel(field)}
                                    </span>
                                    <span class="truncate">${field.to}</span>
                                  `,
                                )}
                              </div>
                            </details>
                          `
                        : nothing
                    }
                  </div>
                `;
              },
            },
            {
              customID: 'findings',
              columnName: 'Hinweise',
              columnWidth: 'minmax(18rem, 1.6fr)',
              selectionSet: { severity: 1, findings: { severity: 1, code: 1, message: 1 } },
              renderItem: (strategy: PersonImportStrategy) => {
                const findings = [...(strategy.findings ?? [])].sort((a, b) => (b.severity ?? 0) - (a.severity ?? 0));
                const [worst] = findings;
                // the row is listed for what it had - say so when it no longer has it
                const resolved = code
                  ? !findings.some(finding => finding.code === code)
                  : severity !== undefined && (strategy.severity ?? ImportFindingSeverity.HINWEIS) < severity;

                return html`
                  <div class="flex w-full min-w-0 flex-col gap-1.5 self-start px-3 py-2 text-sm">
                    ${
                      resolved
                        ? html`
                            <span class="badge badge-success gap-1">
                              <span class="icon-[material-symbols--check-rounded]"></span>
                              Behoben${code ? `: ${IMPORT_FINDING_CATEGORIES[code].label}` : ''}
                            </span>
                          `
                        : nothing
                    }
                    ${
                      worst
                        ? html`
                            <div class="flex items-center gap-1">
                              <span class="badge shrink-0 ${getSeverityUi(worst.severity).badgeClass}">
                                ${findingLabel(worst)}
                              </span>
                              ${
                                findings.length > 1
                                  ? html`<span class="badge badge-ghost badge-sm"
                                      >+${findings.length - 1} weitere</span
                                    >`
                                  : nothing
                              }
                            </div>
                            <p
                              class="line-clamp-2 text-base-content"
                              title=${findings.map(f => f.message).join('\n\n')}
                            >
                              ${worst.message}
                            </p>
                          `
                        : resolved
                          ? nothing
                          : html`<span class="text-base-content/70">—</span>`
                    }
                  </div>
                `;
              },
            },
            {
              customID: 'quickFix',
              columnName: 'Korrektur',
              columnWidth: 'minmax(16rem, 1.4fr)',
              selectionSet: { ...ROW_EDIT_SELECTION, resolvedId: 1, matchedBy: 1 },
              renderItem: (strategy: PersonImportStrategy) => html`
                <div class="flex w-full min-w-0 flex-col gap-1 self-start px-3 py-2 text-sm">
                  ${
                    /**
                     * The found person is the right one: the same as picking them by hand - the row is
                     * never matched again, the name finding goes, and the row is approved.
                     */
                    quickFixConfirmsPerson && strategy.resolvedId
                      ? strategy.matchedBy === 'resolvedId'
                        ? html`<span class="badge badge-success gap-1">
                            <span class="icon-[material-symbols--check-rounded]"></span>
                            Person bestätigt
                          </span>`
                        : html`<button
                            class="btn btn-sm btn-soft btn-success w-fit"
                            @click=${async () => {
                              try {
                                await PersonImportAPI.assignPerson(
                                  strategy._id,
                                  strategy.resolvedId,
                                )(selectionSet(() => PersonImportStrategy, 2));
                              } catch (error) {
                                void SgSnackbar.showError(error instanceof Error ? error.message : String(error));
                              }
                              rerenderRows({});
                            }}
                          >
                            <span class="icon-[material-symbols--check-rounded] text-lg"></span>
                            Person ist korrekt
                          </button>`
                      : nothing
                  }
                  ${quickFixColumns.map(column => {
                    const field = EDITABLE_COLUMNS.find(candidate => candidate.column === column)?.field;
                    if (!field) return nothing;
                    const current = importValueToCsv(strategy.rawImportData ?? new PersonImportData({}), field);

                    return TextInput({
                      value: current,
                      placeholder: IMPORT_FIELD_LABELS[field] ?? column,
                      ariaLabel: IMPORT_FIELD_LABELS[field] ?? column,
                      onPicked: async value => {
                        if ((value ?? '') === current) return;
                        await editImportRow(strategy, [{ column, value: value ?? '' }]).catch(() => null);
                        rerenderRows({});
                      },
                    });
                  })}
                </div>
              `,
            },
            {
              customID: 'certainty',
              columnName: 'Sicherheit',
              columnWidth: '120px',
              selectionSet: { matchConfidence: 1, matchedBy: 1 },
              renderItem: (strategy: PersonImportStrategy) => {
                const { confidence, badgeClass } = getConfidenceUi(strategy.matchConfidence);

                return html`
                  <div class="flex flex-col items-start gap-1 self-start px-3 py-2 text-sm">
                    <span class="badge ${badgeClass}">${Math.round(confidence * 100)} %</span>
                    <span class="text-base-content/70">
                      ${strategy.matchedBy ? (MATCHED_BY_LABEL[strategy.matchedBy] ?? strategy.matchedBy) : 'niemand passt'}
                    </span>
                  </div>
                `;
              },
            },
            {
              customID: 'actions',
              columnName: '',
              columnWidth: '100px',
              // everything the details dialog reads - this column is in every table that opens it
              selectionSet: ROW_DIALOG_SELECTION,
              renderItem: (strategy: PersonImportStrategy) => html`
                <div class="flex w-full justify-end gap-0.5 self-start px-2 py-1.5">
                  <button
                    class="btn btn-ghost btn-sm btn-square"
                    title="Details ansehen und Werte korrigieren"
                    aria-label="Details ansehen und Werte korrigieren"
                    @click=${async () => {
                      if (await openPersonImportRowEditDialog(strategy)) rerenderRows({});
                    }}
                  >
                    <span class="icon-[material-symbols--edit-outline] text-lg"></span>
                  </button>
                  ${
                    strategy.resolvedId
                      ? html`
                          <a
                            class="btn btn-ghost btn-sm btn-square"
                            href="/personen/${strategy.resolvedId}"
                            target="_blank"
                            rel="noopener"
                            title="Person in neuem Tab öffnen"
                            aria-label="Person in neuem Tab öffnen"
                          >
                            <span class="icon-[material-symbols--open-in-new] text-lg"></span>
                          </a>
                        `
                      : nothing
                  }
                </div>
              `,
            },
            {
              fieldName: 'error',
              columnName: 'Fehler beim Import',
              columnWidth: 'minmax(16rem, 1.4fr)',
              selectionSet: { error: 1 },
              renderItem: (strategy: PersonImportStrategy) => html`
                <div class="self-start px-3 py-2">
                  ${strategy.error ? html`<span class="text-sm text-error">${strategy.error}</span>` : nothing}
                </div>
              `,
            },
          ],
        } satisfies DataViewOptions<typeof PersonImportStrategy>,
      ],
      [props.processId, props.fields, severity, code, search],
    );

    const options = useMemo(() => [buildFullOptions(dataViewOptions)], [dataViewOptions]);

    return html`${DataTable({ options: options.dataViewOptions })}`;
  },
);
